import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/Card';
import { ArrowLeft, Save, Eye, EyeOff, Link2 } from 'lucide-react';
import { getGrocery, updateGrocery, getResortLocations, listFoodTags, getGroceryTypeOptions, uploadGroceryFile, publishGrocery, unpublishGrocery } from '../services/resortAdminApi';
import { TimetableEditor } from '../components/TimetableEditor';
import { DeliveryTypesSection, DeliveryTypesConfig } from '../components/DeliveryTypesSection';
import { DeliveryZoneMap } from '../components/DeliveryZoneMap';
import { useAuth } from '../context/AuthContext';
import { validateCoordinates } from '../utils/coordinateValidation';
import { Toast } from '../components/ui/Toast';
import { normalizeScheduledDeliveryConfig } from '../utils/scheduledDeliveryConfig';
import { buildGroceryDeepLink } from '../utils/oneLink';

interface GroceryFoodTag {
  name: string;
  food_tag_name: string;
}

interface DeliveryZoneEntry {
  resort: string;
  delivery_zone_polygon?: { lat: number; lng: number }[] | null;
}

interface GroceryDetails {
  name: string;
  grocery_name: string;
  resort_location: string;
  grocery_description?: string;
  grocery_coordinates?: string;
  grocery_type?: string;
  grocery_address?: string;
  grocery_image?: string;
  grocery_food_tags?: string[];
  working_timetable: {
    weekly: Record<string, { periods: { start: string; end: string }[]; non_business: boolean; is_24_7?: boolean }>;
    special_days: Array<{
      start_date: string;
      end_date: string;
      periods: { start: string; end: string }[];
      non_business: boolean;
    }>;
  } | null;
  is_active: number;
  is_published?: number;
  is_featured?: number;
  notes?: string;
  delivery_zone_polygon?: { lat: number; lng: number }[] | null;
  delivery_zones?: DeliveryZoneEntry[];
  click_and_collect?: number;
  delivery_enabled?: number;
  delivery_scheduling_enabled?: number;
  click_and_collect_scheduling_enabled?: number;
  preorder_when_closed?: number;
  delivery_schedule?: any;
  delivery_scheduled_config?: any;
  click_and_collect_schedule?: any;
  click_and_collect_scheduled_config?: any;
  // Legacy fields for backward compat
  scheduled_delivery_enabled?: number;
  scheduled_delivery_config?: any;
  off_now?: number;
  auto_reset_off_now?: number;
  min_order_value?: number;
}

export const GroceryDetails: React.FC = () => {
  const { name } = useParams<{ name: string }>();
  const navigate = useNavigate();
  const { user, isReadOnly } = useAuth();
  const isSuperAdmin = user?.role === 'Resort Super Admin' || user?.role === 'Supervisor';
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [grocery, setGrocery] = useState<GroceryDetails | null>(null);
  const [resortLocations, setResortLocations] = useState<Array<{ value: string; label: string }>>([]);
  const [formData, setFormData] = useState({
    resort_location: '',
    grocery_name: '',
    grocery_description: '',
    grocery_coordinates: '',
    grocery_type: '',
    grocery_address: '',
    is_active: 1,
    notes: '',
    est_delivery_time: 50,
    pricing_tier: '',
    off_now: 0,
    auto_reset_off_now: 1,
    min_order_value: '' as string | number,
    is_featured: 0,
  });
  const [groceryFoodTags, setGroceryFoodTags] = useState<string[]>([]);
  const [availableGroceryTags, setAvailableGroceryTags] = useState<GroceryFoodTag[]>([]);
  const [selectedTagToAdd, setSelectedTagToAdd] = useState<string>('');
  const [groceryTypeOptions, setGroceryTypeOptions] = useState<string[]>([]);
  const [timetable, setTimetable] = useState<GroceryDetails['working_timetable']>(null);
  const [groceryImage, setGroceryImage] = useState<string | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [deliveryTypesConfig, setDeliveryTypesConfig] = useState<DeliveryTypesConfig>({
    delivery_enabled: 0,
    click_and_collect: 1,
    delivery_scheduling_enabled: 0,
    click_and_collect_scheduling_enabled: 1,
    delivery_schedule: null,
    click_and_collect_schedule: null,
    delivery_scheduled_config: null,
    click_and_collect_scheduled_config: null,
  });
  const [preorderWhenClosed, setPreorderWhenClosed] = useState<number>(0);
  const [deliveryZones, setDeliveryZones] = useState<DeliveryZoneEntry[]>([]);
  const [deliveryZone, setDeliveryZone] = useState<{ lat: number; lng: number }[] | null>(null);
  const [clearDeliveryZone, setClearDeliveryZone] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [errorModal, setErrorModal] = useState<{ show: boolean; title: string; errors: string[] }>({ show: false, title: '', errors: [] });

  // Load resort locations
  useEffect(() => {
    const loadResortLocations = async () => {
      try {
        const result = await getResortLocations();
        if (result && result.resort_locations) {
          setResortLocations(result.resort_locations);
        }
      } catch (error) {
        console.error('Error loading resort locations:', error);
      }
    };
    loadResortLocations();
  }, []);

  // Load grocery food tags
  useEffect(() => {
    const loadGroceryTags = async () => {
      try {
        const tags = await listFoodTags(undefined, true);
        setAvailableGroceryTags(tags || []);
      } catch (error) {
        console.error('Error loading grocery food tags:', error);
      }
    };
    loadGroceryTags();
  }, []);

  // Load grocery type options
  useEffect(() => {
    const loadGroceryTypeOptions = async () => {
      try {
        const options = await getGroceryTypeOptions();
        setGroceryTypeOptions(options || []);
      } catch (error) {
        console.error('Error loading grocery type options:', error);
      }
    };
    loadGroceryTypeOptions();
  }, []);

  // Load grocery details
  useEffect(() => {
    const loadGrocery = async () => {
      if (!name) return;
      setLoading(true);
      try {
        const result = await getGrocery(name);
        setGrocery(result);
        setFormData({
          resort_location: result.resort_location || '',
          grocery_name: result.grocery_name || '',
          grocery_description: result.grocery_description || '',
          grocery_coordinates: result.grocery_coordinates || '',
          grocery_type: result.grocery_type || '',
          grocery_address: result.grocery_address || '',
          is_active: typeof result.is_active === 'number' ? result.is_active : 1,
          notes: result.notes || '',
          est_delivery_time: result.est_delivery_time || 50,
          pricing_tier: result.pricing_tier || '',
          off_now: result.off_now ?? 0,
          auto_reset_off_now: result.auto_reset_off_now ?? 1,
          min_order_value: result.min_order_value ?? '',
          is_featured: result.is_featured ?? 0,
        });
        setGroceryFoodTags(result.grocery_food_tags || []);
        // Set grocery image if it exists
        if (result.grocery_image) {
          setGroceryImage(result.grocery_image);
        } else {
          setGroceryImage(null);
        }
        // Initialize timetable with defaults if not present
        if (result.working_timetable) {
          setTimetable(result.working_timetable);
        } else {
          setTimetable({
            weekly: {
              monday: { periods: [{ start: '07:00', end: '21:30' }], non_business: false },
              tuesday: { periods: [{ start: '07:00', end: '21:30' }], non_business: false },
              wednesday: { periods: [{ start: '07:00', end: '21:30' }], non_business: false },
              thursday: { periods: [{ start: '07:00', end: '21:30' }], non_business: false },
              friday: { periods: [{ start: '07:00', end: '21:30' }], non_business: false },
              saturday: { periods: [{ start: '07:00', end: '21:30' }], non_business: false },
              sunday: { periods: [{ start: '07:00', end: '21:30' }], non_business: false },
            },
            special_days: [],
          });
        }
        setDeliveryTypesConfig({
          delivery_enabled: result.delivery_enabled ?? (result.click_and_collect ? 0 : 1),
          click_and_collect: result.click_and_collect ?? 0,
          delivery_scheduling_enabled: result.delivery_scheduling_enabled ?? result.scheduled_delivery_enabled ?? 0,
          click_and_collect_scheduling_enabled: result.click_and_collect_scheduling_enabled ?? 0,
          delivery_schedule: result.delivery_schedule || null,
          click_and_collect_schedule: result.click_and_collect_schedule || null,
          delivery_scheduled_config: normalizeScheduledDeliveryConfig(
            result.delivery_scheduled_config || result.scheduled_delivery_config,
          ),
          click_and_collect_scheduled_config: normalizeScheduledDeliveryConfig(
            result.click_and_collect_scheduled_config,
          ),
        });
        setPreorderWhenClosed(result.preorder_when_closed ?? 0);
        // Load delivery zones (multi-resort)
        if (result.delivery_zones && result.delivery_zones.length > 0) {
          const zones: DeliveryZoneEntry[] = result.delivery_zones.map((dz: any) => {
            let polygon = dz.delivery_zone_polygon;
            if (typeof polygon === 'string') {
              try { polygon = JSON.parse(polygon); } catch { polygon = null; }
            }
            return { resort: dz.resort || '', delivery_zone_polygon: polygon };
          });
          setDeliveryZones(zones);
          setDeliveryZone(zones[0]?.delivery_zone_polygon || null);
          setClearDeliveryZone(false);
        } else if (result.delivery_zone_polygon) {
          let polygon = result.delivery_zone_polygon;
          if (typeof polygon === 'string') {
            try { polygon = JSON.parse(polygon); } catch { polygon = null; }
          }
          if (Array.isArray(polygon) && polygon.length >= 3) {
            setDeliveryZones([{ resort: result.resort_location || '', delivery_zone_polygon: polygon }]);
            setDeliveryZone(polygon);
          } else {
            setDeliveryZones([]);
            setDeliveryZone(null);
          }
          setClearDeliveryZone(false);
        } else {
          setDeliveryZones([]);
          setDeliveryZone(null);
          setClearDeliveryZone(false);
        }
      } catch (error: any) {
        setError(error.message || 'Failed to load grocery details');
      } finally {
        setLoading(false);
      }
    };
    loadGrocery();
  }, [name]);

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || isUploading) {
      e.target.value = '';
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      e.target.value = '';
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image size must be less than 5MB');
      e.target.value = '';
      return;
    }

    setIsUploading(true);
    setUploadingImage(true);
    setError('');
    setSuccess('');

    try {
      // Upload file and attach to grocery document
      const result = await uploadGroceryFile(file, name || undefined);
      if (result.file_url) {
        // Immediately update the grocery document with the new image URL
        try {
          await updateGrocery({
            grocery_id: name!,
            grocery_image: result.file_url,
          });
          setGroceryImage(result.file_url);
          setSuccess('Image uploaded successfully');
          setTimeout(() => setSuccess(''), 3000);
        } catch (updateError: any) {
          setGroceryImage(result.file_url);
          setError(updateError.message || 'Image uploaded but failed to save. Please save the form.');
        }
      } else {
        setError('Failed to upload image');
      }
    } catch (error: any) {
      setError(error.message || 'Failed to upload image');
    } finally {
      setIsUploading(false);
      setUploadingImage(false);
      e.target.value = '';
    }
  };

  const handleRemoveImage = async () => {
    if (!name) return;

    try {
      await updateGrocery({
        grocery_id: name,
        grocery_image: '',
      });
      setGroceryImage(null);
      setSuccess('Image removed successfully');
      setTimeout(() => setSuccess(''), 3000);
      const result = await getGrocery(name);
      setGrocery(result);
    } catch (error: any) {
      setError(error.message || 'Failed to remove image');
    }
  };

  const getImageUrl = (imageUrl: string | null): string => {
    if (!imageUrl) return '';

    if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
      return imageUrl;
    }

    if (imageUrl.startsWith('/files/')) {
      const base = (import.meta.env.VITE_API_BASE_URL && import.meta.env.VITE_API_BASE_URL.trim()) || window.location.origin;
      return `${base}${imageUrl}`;
    }

    const base = (import.meta.env.VITE_API_BASE_URL && import.meta.env.VITE_API_BASE_URL.trim()) || window.location.origin;
    return `${base}/files/${imageUrl}`;
  };

  const handleCopyDeepLink = async () => {
    if (!name) return;
    const url = buildGroceryDeepLink({
      groceryId: name,
      groceryName: grocery?.grocery_name,
    });
    try {
      await navigator.clipboard.writeText(url);
      setSuccess('Deep link copied to clipboard');
      setTimeout(() => setSuccess(''), 3000);
    } catch {
      // Clipboard API unavailable (insecure context / old browser)
      window.prompt('Copy the deep link:', url);
    }
  };

  const handlePublishToggle = async () => {
    if (!name || !grocery) return;
    setPublishing(true);
    setError('');
    setSuccess('');
    try {
      if (grocery.is_published) {
        const result = await unpublishGrocery(name);
        if (result.ok === false) {
          setErrorModal({ show: true, title: 'Cannot Unpublish Grocery', errors: result.errors || [result.error || 'Unknown error'] });
          return;
        }
        setSuccess('Grocery has been unpublished');
        setGrocery({ ...grocery, is_published: 0 });
      } else {
        const result = await publishGrocery(name);
        if (result.ok === false) {
          setErrorModal({ show: true, title: 'Cannot Publish Grocery', errors: result.errors || [result.error || 'Unknown error'] });
          return;
        }
        setSuccess('Grocery has been published');
        setGrocery({ ...grocery, is_published: 1 });
      }
    } catch (error: any) {
      setError(error.message || 'Failed to update publish status');
    } finally {
      setPublishing(false);
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name) return;

    // Validate required fields
    if (!formData.grocery_name?.trim()) {
      setError('Grocery Name is required');
      return;
    }
    if (!formData.grocery_address?.trim()) {
      setError('Grocery Address is required');
      return;
    }
    if (!formData.grocery_description?.trim()) {
      setError('Grocery Description is required');
      return;
    }
    const coordsError = validateCoordinates(formData.grocery_coordinates || '');
    if (coordsError) {
      setError(coordsError);
      return;
    }
    if (!formData.grocery_type?.trim()) {
      setError('Grocery Type is required');
      return;
    }
    if (!formData.pricing_tier) {
      setError('Pricing Tier is required');
      return;
    }
    if (!timetable) {
      setError('Working Timetable is required');
      return;
    }

    if (
      !deliveryTypesConfig.delivery_enabled
      && !deliveryTypesConfig.delivery_scheduling_enabled
      && !deliveryTypesConfig.click_and_collect
      && !deliveryTypesConfig.click_and_collect_scheduling_enabled
    ) {
      const msg = 'At least one delivery option must be enabled';
      setError(msg);
      setToast({ message: msg, type: 'error' });
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      await updateGrocery({
        grocery_id: name,
        resort_location: formData.resort_location || undefined,
        grocery_name: formData.grocery_name,
        grocery_address: formData.grocery_address,
        grocery_description: formData.grocery_description,
        grocery_coordinates: formData.grocery_coordinates,
        grocery_type: formData.grocery_type,
        grocery_image: groceryImage !== null && groceryImage !== undefined ? groceryImage : '',
        grocery_food_tags: groceryFoodTags,
        working_timetable: JSON.stringify(timetable),
        is_active: formData.is_active,
        notes: formData.notes,
        delivery_zones: deliveryZones.length > 0
          ? deliveryZones.map(dz => ({
              resort: dz.resort,
              delivery_zone_polygon: dz.delivery_zone_polygon ?? null,
            }))
          : undefined,
        delivery_zone_polygon: deliveryZones.length === 0 ? deliveryZone : undefined,
        clear_delivery_zone: clearDeliveryZone,
        est_delivery_time: formData.est_delivery_time,
        pricing_tier: formData.pricing_tier,
        off_now: formData.off_now,
        auto_reset_off_now: formData.auto_reset_off_now,
        min_order_value: formData.min_order_value !== '' ? Number(formData.min_order_value) : 0,
        is_featured: formData.is_featured,
        click_and_collect: deliveryTypesConfig.click_and_collect,
        delivery_enabled: deliveryTypesConfig.delivery_enabled,
        delivery_scheduling_enabled: deliveryTypesConfig.delivery_scheduling_enabled,
        click_and_collect_scheduling_enabled: deliveryTypesConfig.click_and_collect_scheduling_enabled,
        preorder_when_closed: preorderWhenClosed,
        delivery_schedule: deliveryTypesConfig.delivery_schedule ? JSON.stringify(deliveryTypesConfig.delivery_schedule) : undefined,
        click_and_collect_schedule: deliveryTypesConfig.click_and_collect_schedule ? JSON.stringify(deliveryTypesConfig.click_and_collect_schedule) : undefined,
        delivery_scheduled_config: deliveryTypesConfig.delivery_scheduled_config ? JSON.stringify(deliveryTypesConfig.delivery_scheduled_config) : undefined,
        click_and_collect_scheduled_config: deliveryTypesConfig.click_and_collect_scheduled_config ? JSON.stringify(deliveryTypesConfig.click_and_collect_scheduled_config) : undefined,
      });
      setSuccess('Grocery updated successfully');
      setToast({ message: 'Grocery updated successfully', type: 'success' });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error: any) {
      const errMsg = error.message || 'Failed to update grocery';
      setError(errMsg);
      setToast({ message: errMsg, type: 'error' });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-500">Loading grocery details...</div>
      </div>
    );
  }

  if (!grocery) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-red-500">Grocery not found</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/groceries')}
            className="text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <div className="flex items-center space-x-3">
              <h1 className="text-2xl font-semibold text-gray-800">Edit Grocery</h1>
              <span className={`px-2 py-1 text-xs rounded-full ${grocery.is_published ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                {grocery.is_published ? 'Published' : 'Draft'}
              </span>
            </div>
            <p className="mt-1 text-sm text-gray-600">{grocery.grocery_name}</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <button
            type="button"
            onClick={handleCopyDeepLink}
            className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-alps-primary"
          >
            <Link2 className="h-4 w-4 mr-2" />
            Copy Deep Link
          </button>
          {isSuperAdmin && (
            <button
              type="button"
              onClick={handlePublishToggle}
              disabled={isReadOnly || publishing}
              className={`inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white ${
                grocery.is_published
                  ? 'bg-yellow-600 hover:bg-yellow-700'
                  : 'bg-green-600 hover:bg-green-700'
              } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-alps-primary disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {grocery.is_published ? (
                <>
                  <EyeOff className="h-4 w-4 mr-2" />
                  {publishing ? 'Unpublishing...' : 'Unpublish'}
                </>
              ) : (
                <>
                  <Eye className="h-4 w-4 mr-2" />
                  {publishing ? 'Publishing...' : 'Publish'}
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border-l-4 border-green-500 p-4">
          <p className="text-sm text-green-700">{success}</p>
        </div>
      )}

      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
      )}

      <form onSubmit={handleSave} className="space-y-6">
        <Card>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Resort Location</label>
              <select
                value={formData.resort_location}
                onChange={(e) => setFormData({ ...formData, resort_location: e.target.value })}
                disabled={isReadOnly}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                <option key="_empty_resort" value="">Select resort location</option>
                {resortLocations.map((loc, index) => (
                  <option key={loc.value || `loc-${index}`} value={loc.value}>
                    {loc.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Grocery Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.grocery_name}
                onChange={(e) => setFormData({ ...formData, grocery_name: e.target.value })}
                disabled={isReadOnly}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Grocery Address <span className="text-red-500">*</span>
              </label>
              <textarea
                value={formData.grocery_address}
                onChange={(e) => setFormData({ ...formData, grocery_address: e.target.value })}
                rows={3}
                disabled={isReadOnly}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Grocery Description <span className="text-red-500">*</span>
              </label>
              <textarea
                value={formData.grocery_description}
                onChange={(e) => setFormData({ ...formData, grocery_description: e.target.value })}
                rows={4}
                disabled={isReadOnly}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                placeholder="Describe the grocery..."
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Grocery Coordinates <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.grocery_coordinates}
                onChange={(e) => setFormData({ ...formData, grocery_coordinates: e.target.value })}
                disabled={isReadOnly}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                placeholder="e.g., 45.8992, 6.1294"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Grocery Type <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.grocery_type}
                onChange={(e) => setFormData({ ...formData, grocery_type: e.target.value })}
                disabled={isReadOnly}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                required
              >
                <option key="_empty_type" value="">Select grocery type</option>
                {groceryTypeOptions.map((option, index) => (
                  <option key={option || `type-${index}`} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Pricing Tier <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.pricing_tier}
                onChange={(e) => setFormData({ ...formData, pricing_tier: e.target.value })}
                disabled={isReadOnly}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                required
              >
                <option value="">Select pricing tier</option>
                <option value="€">€</option>
                <option value="€€">€€</option>
                <option value="€€€">€€€</option>
              </select>
            </div>

            {/* "Exclusive choice" (is_featured) removed — the Home-screen promo is now
                handled by Banner Campaigns. The field is kept dormant in the backend. */}

            {/* Delivery Types */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Delivery Types</label>
              <DeliveryTypesSection
                config={deliveryTypesConfig}
                onChange={setDeliveryTypesConfig}
                workingTimetable={timetable}
                readOnly={isReadOnly}
              />
              <div className="mt-3">
                <div className="flex items-center space-x-2">
                  <input
                    id="detail-preorder-when-closed"
                    type="checkbox"
                    checked={!!preorderWhenClosed}
                    onChange={(e) => setPreorderWhenClosed(e.target.checked ? 1 : 0)}
                    disabled={isReadOnly}
                    className="h-4 w-4 text-alps-primary border-gray-300 rounded disabled:cursor-not-allowed"
                  />
                  <label htmlFor="detail-preorder-when-closed" className="text-sm text-gray-700">
                    Pre-order while closed
                  </label>
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  Customers can check out scheduled orders while the store is closed; the "New Order"
                  notification is held back and sent at opening time. Requires a scheduled delivery option above.
                  When off, a closed store is browse-only.
                </p>
              </div>
            </div>

            {(deliveryTypesConfig.delivery_enabled || deliveryTypesConfig.delivery_scheduling_enabled) ? (
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Estimated Delivery Time, min <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  min={30}
                  required
                  value={formData.est_delivery_time}
                  onChange={(e) => setFormData({ ...formData, est_delivery_time: parseInt(e.target.value) || 50 })}
                  disabled={isReadOnly}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
                <p className="mt-1 text-xs text-gray-500">Minimum 30 minutes</p>
              </div>
            ) : null}

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Minimum Order Value, &euro; (optional)
              </label>
              <input
                type="number"
                min={0}
                step={0.01}
                value={formData.min_order_value}
                onChange={(e) => setFormData({ ...formData, min_order_value: e.target.value === '' ? '' : parseFloat(e.target.value) })}
                disabled={isReadOnly}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                placeholder="e.g., 15.00"
              />
              <p className="mt-1 text-xs text-gray-500">Leave empty for no minimum</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Grocery Image
              </label>
              {groceryImage ? (
                <div className="mt-2 space-y-2">
                  <div className="relative inline-block" key={`img-${groceryImage}`}>
                    <img
                      src={getImageUrl(groceryImage)}
                      alt="Grocery"
                      className="h-32 w-32 object-cover rounded-md border border-gray-300"
                      onError={(e) => {
                        const img = e.target as HTMLImageElement;
                        const parent = img.parentElement;
                        if (parent && !parent.querySelector('.image-error-placeholder')) {
                          img.style.display = 'none';
                          const placeholder = document.createElement('div');
                          placeholder.className = 'image-error-placeholder h-32 w-32 flex items-center justify-center bg-gray-100 border border-gray-300 rounded-md text-gray-400 text-xs';
                          placeholder.textContent = 'Image not found';
                          parent.appendChild(placeholder);
                        }
                      }}
                      onLoad={(e) => {
                        const img = e.target as HTMLImageElement;
                        const parent = img.parentElement;
                        if (parent) {
                          const placeholder = parent.querySelector('.image-error-placeholder');
                          if (placeholder) {
                            placeholder.remove();
                          }
                          img.style.display = 'block';
                        }
                      }}
                    />
                  </div>
                  <div className="flex gap-2">
                    <label className={`inline-flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed ${isReadOnly ? 'opacity-50 cursor-not-allowed pointer-events-none' : ''}`}>
                      {uploadingImage ? 'Uploading...' : 'Change Image'}
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        disabled={isReadOnly || uploadingImage || isUploading}
                        className="hidden"
                      />
                    </label>
                    <button
                      type="button"
                      onClick={handleRemoveImage}
                      disabled={isReadOnly}
                      className="inline-flex items-center px-3 py-2 border border-red-300 rounded-md shadow-sm text-sm font-medium text-red-700 bg-white hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ) : (
                <div className="mt-2">
                  <label className={`inline-flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed ${isReadOnly ? 'opacity-50 cursor-not-allowed pointer-events-none' : ''}`}>
                    {uploadingImage ? 'Uploading...' : 'Upload Image'}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      disabled={isReadOnly || uploadingImage || isUploading}
                      className="hidden"
                    />
                  </label>
                  <p className="mt-1 text-xs text-gray-500">Max size: 5MB. Supported formats: JPG, PNG, GIF</p>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Grocery Tags (max 2)
              </label>
              {/* Selected Tags as Chips */}
              {groceryFoodTags.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-3">
                  {groceryFoodTags.map((tag) => (
                    <span
                      key={tag}
                      className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800 ${isReadOnly ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:bg-green-200'}`}
                      onClick={() => !isReadOnly && setGroceryFoodTags(groceryFoodTags.filter((t) => t !== tag))}
                    >
                      {tag}
                      {!isReadOnly && <span className="ml-2 text-green-600">×</span>}
                    </span>
                  ))}
                </div>
              )}
              {/* Dropdown to Add Tags */}
              <select
                value={selectedTagToAdd}
                onChange={(e) => {
                  const tagName = e.target.value;
                  if (tagName && !groceryFoodTags.includes(tagName) && groceryFoodTags.length < 2) {
                    setGroceryFoodTags([...groceryFoodTags, tagName]);
                    setSelectedTagToAdd('');
                  }
                }}
                disabled={isReadOnly || groceryFoodTags.length >= 2}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                <option key="_empty_tag" value="">
                  {groceryFoodTags.length >= 2 ? 'Maximum 2 tags selected' : 'Select a tag to add'}
                </option>
                {availableGroceryTags
                  .filter((tag) => !groceryFoodTags.includes(tag.food_tag_name))
                  .map((tag, index) => (
                    <option key={tag.name || `tag-${index}`} value={tag.food_tag_name}>
                      {tag.food_tag_name}
                    </option>
                  ))}
              </select>
              {groceryFoodTags.length >= 2 && (
                <p className="mt-1 text-sm text-gray-500">Maximum of 2 Grocery Tags allowed</p>
              )}
            </div>

            <div className="flex items-center space-x-2">
              <input
                id="grocery-active"
                type="checkbox"
                checked={!!formData.is_active}
                onChange={(e) =>
                  setFormData({ ...formData, is_active: e.target.checked ? 1 : 0 })
                }
                disabled={isReadOnly}
                className="h-4 w-4 text-alps-primary border-gray-300 rounded disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
              <label htmlFor="grocery-active" className="text-sm text-gray-700">
                Active
              </label>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Notes
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={3}
                disabled={isReadOnly}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
            </div>

            {(deliveryTypesConfig.delivery_enabled || deliveryTypesConfig.delivery_scheduling_enabled) && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Delivery Zones
                </label>
                <p className="text-xs text-gray-500 mb-3">
                  Define delivery zone polygons per resort. Orders will only be accepted if the delivery address is inside one of these areas.
                </p>

                {deliveryZones.map((zone, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4 mb-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-medium text-gray-700">Zone {index + 1}</h4>
                      <button
                        type="button"
                        onClick={() => {
                          const updated = deliveryZones.filter((_, i) => i !== index);
                          setDeliveryZones(updated);
                          if (updated.length === 0) setClearDeliveryZone(true);
                        }}
                        disabled={isReadOnly}
                        className="text-sm text-red-600 hover:text-red-800 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Remove
                      </button>
                    </div>
                    <div className="mb-3">
                      <label className="block text-sm font-medium text-gray-600 mb-1">Resort</label>
                      <select
                        value={zone.resort}
                        onChange={(e) => {
                          const updated = [...deliveryZones];
                          updated[index] = { ...updated[index], resort: e.target.value };
                          setDeliveryZones(updated);
                        }}
                        disabled={isReadOnly}
                        className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                      >
                        <option value="">Select resort</option>
                        {resortLocations.map((loc) => (
                          <option key={loc.value} value={loc.value}>{loc.label}</option>
                        ))}
                      </select>
                    </div>
                    <DeliveryZoneMap
                      readOnly={isReadOnly}
                      initialPolygon={zone.delivery_zone_polygon || null}
                      centerCoordinates={formData.grocery_coordinates
                        ? (() => {
                            const parts = formData.grocery_coordinates.split(',').map((p) => parseFloat(p.trim()));
                            if (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
                              return { lat: parts[0], lng: parts[1] };
                            }
                            return undefined;
                          })() || undefined
                        : undefined}
                      onPolygonChange={(poly) => {
                        const updated = [...deliveryZones];
                        updated[index] = { ...updated[index], delivery_zone_polygon: poly };
                        setDeliveryZones(updated);
                        setClearDeliveryZone(false);
                      }}
                    />
                  </div>
                ))}

                <button
                  type="button"
                  onClick={() => {
                    setDeliveryZones([...deliveryZones, { resort: '', delivery_zone_polygon: null }]);
                    setClearDeliveryZone(false);
                  }}
                  disabled={isReadOnly}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  + Add Delivery Zone
                </button>
              </div>
            )}
          </div>
        </Card>

        <Card title={
          <span>
            Working Timetable <span className="text-red-500">*</span>
          </span>
        }>
          <div className="mb-4 space-y-3">
            <div>
              <div className="flex items-center space-x-2">
                <input
                  id="grocery-detail-off-now"
                  type="checkbox"
                  checked={!!formData.off_now}
                  onChange={(e) => setFormData({ ...formData, off_now: e.target.checked ? 1 : 0 })}
                  disabled={isReadOnly}
                  className="h-4 w-4 text-red-600 border-gray-300 rounded disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
                <label htmlFor="grocery-detail-off-now" className="text-sm text-gray-700">
                  Off Now (Force Closed)
                </label>
              </div>
              <p className="mt-1 text-xs text-gray-500">
                When enabled, the grocery will appear as closed regardless of working timetable.
              </p>
            </div>

            <div>
              <div className="flex items-center space-x-2">
                <input
                  id="grocery-detail-auto-reset-off-now"
                  type="checkbox"
                  checked={!!formData.auto_reset_off_now}
                  onChange={(e) => setFormData({ ...formData, auto_reset_off_now: e.target.checked ? 1 : 0 })}
                  disabled={isReadOnly}
                  className="h-4 w-4 text-alps-primary border-gray-300 rounded disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
                <label htmlFor="grocery-detail-auto-reset-off-now" className="text-sm text-gray-700">
                  Auto Reset "Off Now" at Midnight
                </label>
              </div>
              <p className="mt-1 text-xs text-gray-500">
                When enabled, the "Off Now" flag will be automatically reset at midnight so the grocery re-opens the next day according to timetable.
              </p>
            </div>
          </div>

          {timetable ? (
            <TimetableEditor
              readOnly={isReadOnly}
              timetable={timetable}
              onChange={(updatedTimetable) => setTimetable(updatedTimetable)}
            />
          ) : (
            <div className="text-sm text-gray-500">Please configure the working timetable</div>
          )}
        </Card>

        <div className="flex justify-end space-x-2">
          <button
            type="button"
            onClick={() => navigate('/groceries')}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving || isReadOnly}
            className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-alps-primary hover:bg-alps-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-alps-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>

      {/* Error Modal */}
      {errorModal.show && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-red-600">{errorModal.title}</h3>
              <button onClick={() => setErrorModal({ show: false, title: '', errors: [] })} className="text-gray-400 hover:text-gray-600">
                ×
              </button>
            </div>
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">The following requirements are not met:</p>
              <ul className="list-disc list-inside space-y-1">
                {errorModal.errors.map((err, index) => (
                  <li key={index} className="text-sm text-red-700">{err}</li>
                ))}
              </ul>
            </div>
            <div className="flex justify-end">
              <button
                onClick={() => setErrorModal({ show: false, title: '', errors: [] })}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
